import React, { useRef } from "react";
import { X, Printer, Download } from "lucide-react";
import "./Invoice.css";
import logo from "../../Components/Assets/images/tb_logo.png";

const fmtPrice = (v) =>
  parseFloat(v || 0).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (v) => {
  if (!v) return "N/A";
  try {
    return new Date(v).toLocaleDateString("en-NG", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "N/A";
  }
};

export default function Invoice({
  isVisible,
  onClose,
  order,
  invoiceNumber,
  cartItems = [],
  shippingInfo = {},
  billingInfo = {},
}) {
  const printRef = useRef();

  if (!isVisible) return null;

  const tax = order.orderTotal * 0.075;
  const subtotal = order.orderTotal - tax;

  const displayItems =
    Array.isArray(cartItems) && cartItems.length > 0
      ? cartItems
      : (order.cart || []).map((item) => {
          if (typeof item === "string") {
            try {
              return JSON.parse(item);
            } catch {
              return {};
            }
          }
          return item;
        });

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const content = printRef.current.innerHTML;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice #${invoiceNumber || "N/A"} - TrendBay DS</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Inter', Arial, sans-serif; font-size: 13px; color: #111; padding: 32px; }
            .inv-logo { width: 36px; height: auto; }
            .inv-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 16px; border-bottom: 2px solid #00A855; }
            .inv-brand h2 { font-size: 15px; font-weight: 700; color: #080F0D; margin: 6px 0 2px; }
            .inv-brand p { font-size: 12px; color: #666; }
            .inv-meta { text-align: right; }
            .inv-meta h1 { font-size: 22px; font-weight: 800; color: #080F0D; }
            .inv-meta p { font-size: 12px; color: #666; margin-top: 4px; }
            .inv-addresses { display: flex; gap: 32px; margin-bottom: 24px; }
            .inv-address { flex: 1; }
            .inv-address h4 { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #999; margin-bottom: 6px; }
            .inv-address p { font-size: 13px; color: #333; line-height: 1.6; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
            th { background: #fafafa; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: #999; padding: 10px 12px; text-align: left; border-bottom: 1px solid #f0f0f0; }
            td { padding: 10px 12px; font-size: 13px; border-bottom: 1px solid #f8f8f8; color: #333; }
            .inv-total-row td { font-weight: 700; font-size: 14px; color: #080F0D; border-top: 2px solid #f0f0f0; }
            .inv-footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #f0f0f0; display: flex; justify-content: space-between; align-items: center; }
            .inv-footer p { font-size: 12px; color: #888; }
            .inv-paid { background: #e8faf1; color: #00A855; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  return (
    <div className="inv-overlay" onClick={onClose}>
      <div className="inv-modal" onClick={(e) => e.stopPropagation()}>
        {/* Toolbar */}
        <div className="inv-toolbar">
          <span className="inv-toolbar-title">
            Invoice #{invoiceNumber || "N/A"}
          </span>
          <div className="inv-toolbar-actions">
            <button
              className="inv-action-btn"
              onClick={handlePrint}
              title="Print / Save as PDF"
            >
              <Printer size={16} />
              <span>Print / PDF</span>
            </button>
            <button className="inv-close-btn" onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Printable content */}
        <div className="inv-scroll">
          <div className="inv-doc" ref={printRef}>
            {/* Header */}
            <div className="inv-header">
              <div className="inv-brand">
                <img src={logo} className="inv-logo" alt="TrendBay DS" />
                <h2>TrendBay Daily Solutions LTD</h2>
                <p>hello@trendbayds.com · trendbayds.com</p>
              </div>
              <div className="inv-meta">
                <h1>INVOICE</h1>
                <p className="inv-meta-num">#{invoiceNumber || "N/A"}</p>
                <p className="inv-meta-date">
                  Issued: {fmtDate(order.$createdAt)}
                </p>
                <span
                  className={`inv-status inv-status--${order.paymentStatus === "paid" ? "paid" : "pending"}`}
                >
                  {order.paymentStatus === "paid" ? "PAID" : "PENDING"}
                </span>
              </div>
            </div>

            {/* Addresses */}
            {(shippingInfo.fullName || shippingInfo.address) && (
              <div className="inv-addresses">
                <div className="inv-address">
                  <h4>Bill To</h4>
                  <p>{billingInfo.fullName || shippingInfo.fullName}</p>
                  <p>{billingInfo.address || shippingInfo.address}</p>
                  <p>
                    {[
                      billingInfo.city || shippingInfo.city,
                      billingInfo.state || shippingInfo.state,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                  <p>{billingInfo.phoneNumber || shippingInfo.phoneNumber}</p>
                </div>
                <div className="inv-address">
                  <h4>Ship To</h4>
                  <p>{shippingInfo.fullName}</p>
                  <p>{shippingInfo.address}</p>
                  <p>
                    {[shippingInfo.city, shippingInfo.state]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                  <p>{shippingInfo.country}</p>
                </div>
              </div>
            )}

            {/* Items table */}
            <table className="inv-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th style={{ textAlign: "center" }}>Qty</th>
                  <th style={{ textAlign: "right" }}>Unit Price</th>
                  <th style={{ textAlign: "right" }}>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {displayItems.length > 0 ? (
                  displayItems.map((item, i) => (
                    <tr key={i}>
                      <td>{item.title || item.productName || "-"}</td>
                      <td style={{ textAlign: "center" }}>
                        {item.quantity || 1}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        ₦{fmtPrice(item.price)}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        ₦{fmtPrice((item.price || 0) * (item.quantity || 1))}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      style={{ textAlign: "center", color: "#aaa" }}
                    >
                      No items
                    </td>
                  </tr>
                )}
                <tr className="inv-subtotal-row">
                  <td colSpan={3} style={{ textAlign: "right" }}>
                    Subtotal (before tax)
                  </td>
                  <td style={{ textAlign: "right" }}>₦{fmtPrice(subtotal)}</td>
                </tr>
                <tr>
                  <td colSpan={3} style={{ textAlign: "right", color: "#666" }}>
                    VAT (7.5%)
                  </td>
                  <td style={{ textAlign: "right", color: "#666" }}>
                    ₦{fmtPrice(tax)}
                  </td>
                </tr>
                <tr className="inv-total-row">
                  <td colSpan={3} style={{ textAlign: "right" }}>
                    Total
                  </td>
                  <td style={{ textAlign: "right" }}>
                    ₦{fmtPrice(order.orderTotal)}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Footer */}
            <div className="inv-footer">
              <p>
                Order #{order.orderNumber || (order.$id || "").slice(-8)} ·{" "}
                {fmtDate(order.$createdAt)}
              </p>
              <p>Thank you for shopping with TrendBay DS</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
